import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { api, ApiError } from '../../api/client'
import { StatusBadge } from '../../components/StatusBadge'

// ISSUE #10: Job 一覧。limit/before cursor pagination を最低限の UI で対応。
// `before` を stack のように push/pop することで Prev/Next を簡易実装する。
export function JobListPage() {
  const [limit, setLimit] = useState(20)
  // 履歴の最新値が「現在のページの before」。空配列が先頭ページ (before 未指定)。
  const [cursorStack, setCursorStack] = useState<string[]>([])

  const currentBefore = cursorStack[cursorStack.length - 1]

  const query = useQuery({
    queryKey: ['jobs', { limit, before: currentBefore ?? null }],
    queryFn: () => api.listJobs({ limit, before: currentBefore }),
    // ISSUE #10 (Codex review fix): terminal な状態しか含まない page では polling を停止する。
    // (完了済み job ばかりの履歴ページで無駄なリクエストを打たないため)
    refetchInterval: (query) => {
      const jobs = query.state.data?.jobs ?? []
      const allTerminal = jobs.every((j) =>
        ['completed', 'failed', 'cancelled'].includes(j.status),
      )
      return jobs.length > 0 && allTerminal ? false : 5000
    },
    placeholderData: keepPreviousData,
  })

  // 次ページは「現在の最後の row の job.id」を before として渡す方式。
  // (Worker の tuple cursor は job id を受け取る)
  const jobs = query.data?.jobs ?? []
  const lastIdOnPage =
    query.data && jobs.length === limit ? jobs.at(-1)?.id : undefined
  // ISSUE #10 (Codex review fix): 同じ cursor を二重 push しない & loading 中は Next 無効化。
  const canGoNext = Boolean(
    lastIdOnPage &&
      lastIdOnPage !== currentBefore &&
      !query.isFetching &&
      !query.isPlaceholderData,
  )

  const onNext = () => {
    if (canGoNext && lastIdOnPage) {
      setCursorStack((stack) => [...stack, lastIdOnPage])
    }
  }
  const onPrev = () => {
    setCursorStack((stack) => stack.slice(0, -1))
  }
  const onReset = () => setCursorStack([])

  return (
    <section>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Jobs</h1>
          <p className="text-sm text-slate-500">
            Polling every 5s. Click a row to see events.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <label className="flex items-center gap-1">
            <span className="text-slate-600">Limit</span>
            <select
              className="rounded border border-slate-300 bg-white px-2 py-1"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value))
                setCursorStack([])
              }}
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {query.isPending ? (
        <p className="text-slate-500">Loading…</p>
      ) : query.isError ? (
        <ErrorBox error={query.error} />
      ) : query.data.jobs.length === 0 ? (
        <p className="text-slate-500">
          No jobs yet.{' '}
          <Link to="/jobs/new" className="text-blue-600 underline">
            Create one
          </Link>
          .
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">ID</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Project</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Risk</th>
                <th className="px-4 py-2">Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {query.data.jobs.map((job) => (
                <tr key={job.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono text-xs">
                    <Link to={`/jobs/${job.id}`} className="text-blue-700 hover:underline">
                      {job.id.slice(0, 8)}…
                    </Link>
                  </td>
                  <td className="px-4 py-2">{job.type}</td>
                  <td className="px-4 py-2">{job.project ?? '—'}</td>
                  <td className="px-4 py-2">
                    <StatusBadge status={job.status} />
                  </td>
                  <td className="px-4 py-2 text-slate-600">{job.risk_level}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    {new Date(job.updated_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between text-sm">
        <div className="text-slate-500">
          {query.isFetching ? 'Refreshing…' : ' '}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onReset}
            disabled={cursorStack.length === 0}
            className="rounded border border-slate-300 bg-white px-3 py-1 disabled:opacity-50"
          >
            First
          </button>
          <button
            type="button"
            onClick={onPrev}
            disabled={cursorStack.length === 0}
            className="rounded border border-slate-300 bg-white px-3 py-1 disabled:opacity-50"
          >
            Prev
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={!canGoNext}
            className="rounded border border-slate-300 bg-white px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </section>
  )
}

function ErrorBox({ error }: { error: unknown }) {
  const message =
    error instanceof ApiError
      ? `${error.code}: ${error.message}`
      : error instanceof Error
        ? error.message
        : 'Unknown error'
  return (
    <div className="rounded border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
      Failed to load jobs — {message}
    </div>
  )
}
